import Header from "../components/Header";
import Footer from "../components/Footer";
import GlobalMap from "../components/GlobalMap";

function Home() {
  return (
    <div className="antialiased selection:bg-vokaris-green selection:text-vokaris-teal">
      <Header />

      {/* Hero Section */}
      <header className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-white">
        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-v-pattern opacity-60 pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vokaris-blue text-vokaris-teal text-xs font-bold uppercase tracking-wider mb-8 border border-vokaris-teal/10">
            <span className="w-2 h-2 rounded-full bg-vokaris-green animate-pulse"></span>
            Now In Pre-Alpha
          </div>

          <h1 className="text-4xl lg:text-6xl font-bold leading-[1.1] text-vokaris-teal mb-8">
            A Unified Layer for Access, Identity, and Operations.{" "}
            <br className="hidden lg:block" />
            <span className="text-vokaris-teal/60 text-3xl lg:text-4xl ">
              With Infrastructure That Just Works.
            </span>
          </h1>

          <p className="text-lg lg:text-xl text-vokaris-teal/70 mb-10 leading-relaxed max-w-3xl mx-auto">
            Vokaris gives you everything you need — identity, compute
            (bare-metal, virtual machines, and serverless), storage, billing,
            notifications, and app services — all integrated with clean, modern
            SDKs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://hub.vokaris.io"
              className="bg-vokaris-green text-vokaris-teal px-8 py-4 rounded-full text-lg font-bold hover:bg-vokaris-teal hover:text-white hover:scale-105 transition duration-300 shadow-lg shadow-vokaris-green/20"
            >
              Start deploying →
            </a>
            <a
              href="https://docs.vokaris.io"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-vokaris-teal/20 text-vokaris-teal px-8 py-4 rounded-full text-lg font-medium hover:border-vokaris-teal transition duration-300 bg-white"
            >
              Explore Docs
            </a>
          </div>
        </div>
      </header>

      {/* The Platform Promise */}
      <section
        id="platform"
        className="py-20 bg-vokaris-surface border-y border-gray-100"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-vokaris-teal mb-6 font-display">
            The Platform That Lets You Ship
          </h2>
          <p className="text-xl text-vokaris-teal/70 leading-relaxed mb-6">
            Stop piecing together multiple tools and servers. <br />
            Vokaris combines identity, compute, storage, and app infrastructure
            into a single platform — simple, reliable, and ready for solo
            developers, growing teams, and enterprises alike.
          </p>
          <p className="text-vokaris-teal font-bold tracking-wide uppercase text-sm">
            Less overhead. More shipping.
          </p>
        </div>
      </section>

      {/* Global Infrastructure Map */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-vokaris-teal mb-4 font-display">
              Infrastructure
            </h2>
            <p className="text-xl text-vokaris-teal/70 max-w-3xl mx-auto">
              Deploy your applications close to your users with our distributed
              infrastructure
            </p>
          </div>
          <GlobalMap />
        </div>
      </section>

      {/* What Vokaris Gives You (Grid) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-vokaris-teal font-display">
              What Vokaris Gives You
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* SDKs */}
            <div className="p-8 border border-gray-100 rounded-2xl hover-lift bg-white group">
              <div className="w-12 h-12 bg-vokaris-blue rounded-lg flex items-center justify-center mb-6 text-vokaris-teal">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-vokaris-teal mb-3 font-display">
                SDKs Built for Speed
              </h3>
              <p className="text-vokaris-teal/70">
                Integrate identity, compute, storage, and app services with
                minimal effort — frontend and backend SDKs designed to let you
                move fast.
              </p>
            </div>

            {/* Compute */}
            <div className="p-8 border border-gray-100 rounded-2xl hover-lift bg-white group">
              <div className="w-12 h-12 bg-vokaris-blue rounded-lg flex items-center justify-center mb-6 text-vokaris-teal">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-vokaris-teal mb-3 font-display">
                Flexible Compute
              </h3>
              <p className="text-vokaris-teal/70">
                Run your workloads anywhere — choose{" "}
                <span className="text-vokaris-teal font-semibold">
                  bare-metal
                </span>{" "}
                for maximum performance,{" "}
                <span className="text-vokaris-teal font-semibold">
                  virtual machines
                </span>{" "}
                for isolation, or{" "}
                <span className="text-vokaris-teal font-semibold">
                  serverless
                </span>{" "}
                for fast, event-driven execution.
              </p>
            </div>

            {/* Storage */}
            <div className="p-8 border border-gray-100 rounded-2xl hover-lift bg-white group">
              <div className="w-12 h-12 bg-vokaris-blue rounded-lg flex items-center justify-center mb-6 text-vokaris-teal">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-vokaris-teal mb-3 font-display">
                Secure Storage & Secrets
              </h3>
              <p className="text-vokaris-teal/70">
                Encrypted storage, secure secrets management, and reliable
                replication — from side projects to enterprise-grade
                deployments.
              </p>
            </div>

            {/* App Services */}
            <div className="p-8 border border-gray-100 rounded-2xl hover-lift bg-white group">
              <div className="w-12 h-12 bg-vokaris-blue rounded-lg flex items-center justify-center mb-6 text-vokaris-teal">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-vokaris-teal mb-3 font-display">
                Built-in App Services
              </h3>
              <p className="text-vokaris-teal/70">
                Notifications, billing, usage tracking, and onboarding workflows
                — pre-integrated so you can focus on your product, not
                infrastructure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Everyone (List) */}
      <section className="py-20 bg-vokaris-surface">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-vokaris-teal mb-8 font-display">
            Built for Builders
          </h2>
          <p className="text-lg text-vokaris-teal/70 mb-10">
            Everything you need to ship without the busywork. Vokaris handles
            the foundations — you build the product.
          </p>

          <div className="space-y-4">
            {[
              "Identity and access ready out-of-the-box",
              "Flexible compute for APIs, jobs, or functions",
              "Storage and secrets management built-in",
              "Notifications and billing turned on in minutes",
              "Manage tenants, users, and usage from a single dashboard",
            ].map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100"
              >
                <div className="w-6 h-6 rounded-full bg-vokaris-green flex items-center justify-center text-vokaris-teal text-sm font-bold">
                  ✓
                </div>
                <span className="text-vokaris-teal font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Reliability (Dark Section for Contrast) */}
      <section id="enterprise" className="py-24 bg-vokaris-teal text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6 font-display">
                Enterprise-Grade Reliability
              </h2>
              <p className="text-xl text-vokaris-blue/80 mb-8">
                Power for individuals. Stability for teams. Scale for
                enterprises. Modern infrastructure with reliability built in.
              </p>
              <a
                href="https://compliance.vokaris.io/trust/KnHUNzmNAAEAFgAAAZr_uNGX2BjPChK3"
                target="_blank"
                rel="noopener noreferrer"
                className="text-vokaris-green font-bold border-b border-vokaris-green hover:text-white hover:border-white transition pb-1"
              >
                Read about our security →
              </a>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
              <ul className="space-y-6">
                {[
                  {
                    text: "Isolation across tenants and teams",
                    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                  },
                  {
                    text: "Encryption everywhere — at rest and in transit",
                    icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                  },
                  {
                    text: "Predictable performance across compute types",
                    icon: "M13 10V3L4 14h7v7l9-11h-7z",
                  },
                  {
                    text: "Enterprise access controls and auditing",
                    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
                  },
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <svg
                      className="w-6 h-6 text-vokaris-green shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={item.icon}
                      ></path>
                    </svg>
                    <span className="text-vokaris-blue/90">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Vokaris (Cards) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-vokaris-teal mb-12 font-display">
            Why Choose Vokaris
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "SDKs simplify integration",
                desc: "Effortless frontend and backend implementation.",
              },
              {
                title: "Compute that scales",
                desc: "Bare-metal, VMs, and serverless options.",
              },
              {
                title: "One Unified Platform",
                desc: "Replace scattered tools with one cohesive system.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-vokaris-surface p-6 rounded-xl border border-gray-100"
              >
                <h4 className="font-bold text-vokaris-teal mb-2">
                  {item.title}
                </h4>
                <p className="text-sm text-vokaris-teal/60">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Old Way vs Vokaris Way */}
      <section
        id="how-it-works"
        className="py-24 bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-vokaris-blue/10 -z-10"></div>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-vokaris-teal mb-4 font-display">
              The Old Way vs The Vokaris Way
            </h2>
            <p className="text-xl text-vokaris-teal/70 max-w-2xl mx-auto">
              Stop juggling dozens of services. Start shipping.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Old Way */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 relative">
              <div className="absolute -top-4 left-6 bg-gray-400 text-white px-4 py-1 rounded-full text-sm font-bold">
                The Old Way
              </div>
              <div className="space-y-4 mt-4">
                {[
                  { service: "Auth0 / Clerk", purpose: "Identity" },
                  { service: "AWS / GCP / Azure", purpose: "Compute" },
                  { service: "S3 + Vault", purpose: "Storage & Secrets" },
                  { service: "Stripe", purpose: "Billing" },
                  { service: "SendGrid / Twilio", purpose: "Notifications" },
                  { service: "Segment / Mixpanel", purpose: "Analytics" },
                  { service: "Custom code", purpose: "Glue it all together" },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <span className="text-vokaris-teal/80 font-medium">
                        {item.service}
                      </span>
                    </div>
                    <span className="text-sm text-gray-400">
                      {item.purpose}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    Total services to manage:
                  </span>
                  <span className="font-bold text-gray-600">7+ vendors</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-500">Integration time:</span>
                  <span className="font-bold text-gray-600">
                    Weeks to months
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-500">Maintenance overhead:</span>
                  <span className="font-bold text-red-500">High</span>
                </div>
              </div>
            </div>

            {/* Vokaris Way */}
            <div className="bg-vokaris-teal border border-vokaris-teal rounded-2xl p-8 relative">
              <div className="absolute -top-4 left-6 bg-vokaris-green text-vokaris-teal px-4 py-1 rounded-full text-sm font-bold">
                The Vokaris Way
              </div>
              <div className="space-y-4 mt-4">
                {[
                  "Identity & Access Management",
                  "Bare-metal, VMs & Serverless",
                  "Encrypted Storage & Secrets",
                  "Billing & Subscriptions",
                  "Email, SMS & Push Notifications",
                  "Usage Metering & Analytics",
                  "One SDK. Zero glue code.",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-white/10 p-4 rounded-lg border border-white/10"
                  >
                    <svg
                      className="w-5 h-5 text-vokaris-green shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-vokaris-blue/70">
                    Total services to manage:
                  </span>
                  <span className="font-bold text-vokaris-green">
                    1 platform
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-vokaris-blue/70">
                    Integration time:
                  </span>
                  <span className="font-bold text-vokaris-green">
                    Hours to days
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-vokaris-blue/70">
                    Maintenance overhead:
                  </span>
                  <span className="font-bold text-vokaris-green">Minimal</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <p className="text-vokaris-teal/70 mb-6">
              Replace your infrastructure patchwork with one unified platform.
            </p>
            <a
              href="https://hub.vokaris.io"
              className="inline-flex items-center gap-2 bg-vokaris-green text-vokaris-teal px-8 py-4 rounded-full text-lg font-bold hover:bg-vokaris-teal hover:text-white transition duration-300"
            >
              Try Vokaris Free
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Roadmap - Kanban Style */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-vokaris-teal mb-4 font-display">
              The Road Ahead
            </h2>
            <p className="text-xl text-vokaris-teal/70 max-w-2xl mx-auto">
              More tools for builders. Less infrastructure hassle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Live Column */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-vokaris-green/20 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-vokaris-green"></span>
                  <h3 className="font-bold text-vokaris-teal">Live</h3>
                  <span className="ml-auto text-sm text-vokaris-teal/60 bg-white px-2 py-0.5 rounded-full">
                    12
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-[480px] overflow-y-auto">
                {[
                  { title: "Passkeys & WebAuthn", category: "Identity" },
                  { title: "Social logins", category: "Identity" },
                  { title: "Serverless functions", category: "Compute" },
                  { title: "Virtual machines", category: "Compute" },
                  { title: "Object storage", category: "Storage" },
                  { title: "Secrets vault", category: "Storage" },
                  { title: "Email notifications", category: "App Services" },
                  { title: "Usage metering", category: "App Services" },
                  { title: "Subscription management", category: "Billing" },
                  { title: "TypeScript SDK", category: "Developer Experience" },
                  {
                    title: "REST & GraphQL APIs",
                    category: "Developer Experience",
                  },
                  { title: "Multi-tenant workspaces", category: "Platform" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-vokaris-green/30 hover:shadow-sm transition cursor-default"
                  >
                    <p className="text-vokaris-teal font-medium text-sm mb-2">
                      {item.title}
                    </p>
                    <span className="text-xs text-vokaris-teal/50 bg-white px-2 py-1 rounded-full border border-gray-100">
                      {item.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Building Column */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-amber-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                  <h3 className="font-bold text-vokaris-teal">Building</h3>
                  <span className="ml-auto text-sm text-vokaris-teal/60 bg-white px-2 py-0.5 rounded-full">
                    7
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-[480px] overflow-y-auto">
                {[
                  { title: "Enterprise SSO (SAML)", category: "Identity" },
                  { title: "Bare-metal servers", category: "Compute" },
                  { title: "Edge caching", category: "Storage" },
                  { title: "Workflow automation", category: "App Services" },
                  { title: "Usage-based billing", category: "Billing" },
                  { title: "Invoicing", category: "Billing" },
                  { title: "CLI tools", category: "Developer Experience" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-sm transition cursor-default"
                  >
                    <p className="text-vokaris-teal font-medium text-sm mb-2">
                      {item.title}
                    </p>
                    <span className="text-xs text-vokaris-teal/50 bg-white px-2 py-1 rounded-full border border-gray-100">
                      {item.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Planned Column */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                  <h3 className="font-bold text-vokaris-teal">Planned</h3>
                  <span className="ml-auto text-sm text-vokaris-teal/60 bg-white px-2 py-0.5 rounded-full">
                    6
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-[480px] overflow-y-auto">
                {[
                  { title: "Directory sync (SCIM)", category: "Identity" },
                  { title: "GPU instances", category: "Compute" },
                  { title: "Database hosting", category: "Storage" },
                  { title: "Visual email builder", category: "App Services" },
                  { title: "Revenue analytics", category: "Billing" },
                  {
                    title: "Terraform provider",
                    category: "Developer Experience",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition cursor-default"
                  >
                    <p className="text-gray-500 font-medium text-sm mb-2">
                      {item.title}
                    </p>
                    <span className="text-xs text-gray-400 bg-white px-2 py-1 rounded-full border border-gray-100">
                      {item.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Request Feature CTA */}
          <div className="text-center mt-12">
            <p className="text-vokaris-teal/60 text-sm">
              Have a feature request?{" "}
              <a
                href="mailto:hello@vokaris.io"
                className="text-vokaris-teal font-medium hover:text-vokaris-green transition"
              >
                Let us know →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="access" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-vokaris-teal mb-6 font-display">
            Ready to build? <br />
            Start with Vokaris.
          </h2>
          <p className="text-xl text-vokaris-teal/70 mb-10 font-medium">
            Whether you're a solo builder, a growing team, or an enterprise —
            get the infrastructure you need without the complexity you don't.
          </p>
          <a
            href="https://hub.vokaris.io"
            className="bg-vokaris-green text-vokaris-teal px-12 py-5 rounded-full text-lg font-bold hover:bg-vokaris-teal hover:text-white transition duration-300 shadow-lg inline-block"
          >
            Start deploying →
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
